import { useCallback } from 'react';
import { Trigger } from 'client/voladeum/triggers/types';
import { selectModal } from 'ducks/modals/selectors';
import { updateModal } from 'ducks/modals/slices';
import { trigger_default } from 'ducks/trigger/defaults';
import { selectTriggerTypes } from 'ducks/trigger-types/selectors';
import { Button } from 'framework/button';
import { Dropdown } from 'framework/dropdown';
import { Input, InputPercent } from 'framework/Input';
import { Modal } from 'framework/modal';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useTriggerContext } from 'pages/context/TriggerContext';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import cn from '../Modal.module.scss';

export const ManageTriggerModal = () => {
  const dispatch = useAppDispatch();

  const voladeum_trigger_types = useAppSelector(selectTriggerTypes);
  const { is_open, is_update } = useAppSelector(selectModal('trigger_manage'));
  const { trigger, updateTrigger, setTrigger } = useTriggerContext();
  const { updateStrategyTrigger } = useStrategyContext();

  const onSubmit = () => {
    updateStrategyTrigger(trigger);
    onClose();
  };

  const onClose = useCallback(() => {
    setTrigger({ ...trigger_default });
    dispatch(updateModal({ key: 'trigger_manage', data: { is_open: false, is_update: false } }));
  }, [dispatch, setTrigger]);

  const onUpdate = useCallback(
    (entity: Partial<Trigger>) => updateTrigger(entity),
    [updateTrigger]
  );

  const { trigger_name, trigger_type_name, position_pct_formatted, weight_formatted } = trigger;

  return (
    <Modal
      is_open={is_open}
      identifier="trigger_manage"
      width="450px"
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <Modal.Header onClose={onClose} dismiss>
        {is_update ? 'Update Trigger' : 'Create Trigger'}
      </Modal.Header>
      <Modal.Body>
        <Input
          label="Trigger Name"
          value={trigger_name}
          placeholder="(Default)"
          onChange={({ value }) => onUpdate({ trigger_name: value })}
        />
        <Dropdown
          label="Trigger Type *"
          option_key="trigger_type_name"
          options={voladeum_trigger_types}
          onSelect={({ trigger_type_id, trigger_type_name }) =>
            onUpdate({ trigger_type_id, trigger_type_name })
          }
          value={trigger_type_name}
        />
        <div className={cn.row}>
          <InputPercent
            label="Max Position *"
            value={position_pct_formatted}
            onChange={({ float_value, formatted_value }) => {
              onUpdate({ position_pct: float_value, position_pct_formatted: formatted_value });
            }}
          />
        </div>
        <Input
          label="Weight *"
          value={weight_formatted}
          onChange={({ value, formatted_value }) =>
            onUpdate({
              weight: Number(value || 0),
              weight_formatted: formatted_value,
            })
          }
        />
      </Modal.Body>
      <Modal.Footer>
        <div className={cn.flex} />
        <Button type="submit">Ok</Button>
      </Modal.Footer>
    </Modal>
  );
};
